/**
 * WeaveIn.ai Platform - Billing Dependencies API Service
 * TypeScript service layer for billing activity dependencies, groups, and validation
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type DependencyType = 'mutual_exclusion' | 'prerequisite' | 'conditional_prerequisite' | 'bundled_required';
export type ScopeType = 'member' | 'episode' | 'enrollment' | 'program';
export type GroupType = 'mutual_exclusion' | 'alternative' | 'tiered';
export type ViolationSeverity = 'warning' | 'error' | 'blocking';

export interface BillingDependency {
  id: number;
  activity_id: number;
  activity_name: string;
  activity_code?: string;
  target_activity_id: number;
  target_activity_name: string;
  target_activity_code?: string;
  dependency_type: DependencyType;
  scope_type: ScopeType;
  conditions?: Record<string, any>;
  description?: string;
  is_active: boolean;
  effective_date?: string;
  expiration_date?: string;
  created_ts: string;
  created_by?: number;
}

export interface BillingDependencyCreate {
  activity_id: number;
  target_activity_id: number;
  dependency_type: DependencyType;
  scope_type?: ScopeType;
  conditions?: Record<string, any>;
  description?: string;
  effective_date?: string;
  expiration_date?: string;
}

export interface BillingDependencyUpdate {
  dependency_type?: DependencyType;
  scope_type?: ScopeType;
  conditions?: Record<string, any>;
  description?: string;
  is_active?: boolean;
  effective_date?: string;
  expiration_date?: string;
}

export interface EpisodeLimit {
  id: number;
  activity_id: number;
  activity_name: string;
  episode_type: string;
  max_units_per_episode: number;
  episode_identifier_field?: string;
  episode_window_days?: number;
  description?: string;
  is_active: boolean;
  created_ts: string;
}

export interface EpisodeLimitCreate {
  activity_id: number;
  episode_type: string;
  max_units_per_episode: number;
  episode_identifier_field?: string;
  episode_window_days?: number;
  description?: string;
}

export interface ActivityGroup {
  id: number;
  program_id: number;
  group_name: string;
  group_code?: string;
  group_type: GroupType;
  description?: string;
  selection_rule: string;
  scope_type: ScopeType;
  is_active: boolean;
  activities: Array<{
    id: number;
    activity_name: string;
    activity_code?: string;
    priority_order: number;
  }>;
  created_ts: string;
}

export interface ActivityGroupCreate {
  program_id: number;
  group_name: string;
  group_code?: string;
  group_type: GroupType;
  description?: string;
  selection_rule?: string;
  scope_type?: ScopeType;
  activity_ids: number[];
}

export interface ActivityGroupUpdate {
  group_name?: string;
  group_code?: string;
  group_type?: GroupType;
  description?: string;
  selection_rule?: string;
  scope_type?: ScopeType;
  activity_ids?: number[];
  is_active?: boolean;
}

export interface ValidationViolation {
  type: string;
  message: string;
  severity: ViolationSeverity;
  details?: Record<string, any>;
}

export interface ValidationResult {
  is_valid: boolean;
  violations: ValidationViolation[];
  warnings: ValidationViolation[];
}

export interface ValidationRequest {
  member_id: number;
  activity_id: number;
  service_date: string;
  units?: number;
  prior_auth_number?: string;
  episode_identifier?: string;
}

export interface ViolationLog {
  id: number;
  member_id: number;
  claim_id?: number;
  violation_type: string;
  violation_message: string;
  severity: string;
  resolution_status: string;
  resolution_notes?: string;
  resolved_by?: number;
  resolved_at?: string;
  detected_at: string;
  context_data?: Record<string, any>;
}

export interface CSVImportResult {
  imported: number;
  errors: string[];
}

// ============================================================================
// API CLIENT
// ============================================================================

class BillingDependenciesAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  // ========================================================================
  // DEPENDENCY CRUD
  // ========================================================================

  async createDependency(data: BillingDependencyCreate): Promise<BillingDependency> {
    const response = await this.client.post<BillingDependency>('/api/billing-dependencies', data);
    return response.data;
  }

  async getDependency(dependencyId: number): Promise<BillingDependency> {
    const response = await this.client.get<BillingDependency>(
      `/api/billing-dependencies/${dependencyId}`
    );
    return response.data;
  }

  async getDependenciesForActivity(
    activityId: number,
    includeInactive: boolean = false
  ): Promise<BillingDependency[]> {
    const response = await this.client.get<BillingDependency[]>(
      `/api/billing-dependencies/activity/${activityId}`,
      { params: { include_inactive: includeInactive } }
    );
    return response.data;
  }

  async getDependenciesForProgram(
    programId: number,
    dependencyType?: DependencyType,
    includeInactive: boolean = false
  ): Promise<BillingDependency[]> {
    const response = await this.client.get<BillingDependency[]>(
      `/api/billing-dependencies/program/${programId}`,
      { params: { dependency_type: dependencyType, include_inactive: includeInactive } }
    );
    return response.data;
  }

  async updateDependency(
    dependencyId: number,
    data: BillingDependencyUpdate
  ): Promise<BillingDependency> {
    const response = await this.client.put<BillingDependency>(
      `/api/billing-dependencies/${dependencyId}`,
      data
    );
    return response.data;
  }

  async deleteDependency(dependencyId: number): Promise<void> {
    await this.client.delete(`/api/billing-dependencies/${dependencyId}`);
  }

  // ========================================================================
  // EPISODE LIMITS
  // ========================================================================

  async createEpisodeLimit(data: EpisodeLimitCreate): Promise<EpisodeLimit> {
    const response = await this.client.post<EpisodeLimit>(
      '/api/billing-dependencies/episode-limits',
      data
    );
    return response.data;
  }

  async getEpisodeLimitsForActivity(activityId: number): Promise<EpisodeLimit[]> {
    const response = await this.client.get<EpisodeLimit[]>(
      `/api/billing-dependencies/episode-limits/activity/${activityId}`
    );
    return response.data;
  }

  // ========================================================================
  // ACTIVITY GROUPS
  // ========================================================================

  async createActivityGroup(data: ActivityGroupCreate): Promise<ActivityGroup> {
    const response = await this.client.post<ActivityGroup>(
      '/api/billing-dependencies/groups',
      data
    );
    return response.data;
  }

  async getGroupsForProgram(
    programId: number,
    groupType?: GroupType
  ): Promise<ActivityGroup[]> {
    const response = await this.client.get<ActivityGroup[]>(
      `/api/billing-dependencies/groups/program/${programId}`,
      { params: { group_type: groupType } }
    );
    return response.data;
  }

  async updateActivityGroup(
    groupId: number,
    data: ActivityGroupUpdate
  ): Promise<ActivityGroup> {
    const response = await this.client.put<ActivityGroup>(
      `/api/billing-dependencies/groups/${groupId}`,
      data
    );
    return response.data;
  }

  async deleteActivityGroup(groupId: number): Promise<void> {
    await this.client.delete(`/api/billing-dependencies/groups/${groupId}`);
  }

  // ========================================================================
  // VALIDATION
  // ========================================================================

  async validateBillingActivity(request: ValidationRequest): Promise<ValidationResult> {
    const response = await this.client.post<ValidationResult>(
      '/api/billing-dependencies/validate',
      request
    );
    return response.data;
  }

  // ========================================================================
  // VIOLATION LOG
  // ========================================================================

  async getViolationLog(params?: {
    member_id?: number;
    claim_id?: number;
    violation_type?: string;
    resolution_status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ViolationLog[]> {
    const response = await this.client.get<ViolationLog[]>(
      '/api/billing-dependencies/violations',
      { params }
    );
    return response.data;
  }

  async resolveViolation(
    violationId: number,
    resolutionStatus: 'overridden' | 'corrected' | 'dismissed',
    resolutionNotes?: string
  ): Promise<{ message: string; id: number }> {
    const response = await this.client.post<{ message: string; id: number }>(
      `/api/billing-dependencies/violations/${violationId}/resolve`,
      null,
      { params: { resolution_status: resolutionStatus, resolution_notes: resolutionNotes } }
    );
    return response.data;
  }

  // ========================================================================
  // CSV IMPORT/EXPORT
  // ========================================================================

  async importDependenciesCSV(programId: number, file: File): Promise<CSVImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<CSVImportResult>(
      '/api/billing-dependencies/import-csv',
      formData,
      {
        params: { program_id: programId },
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  async exportDependenciesCSV(programId: number): Promise<Blob> {
    const response = await this.client.get(
      `/api/billing-dependencies/export-csv/${programId}`,
      { responseType: 'blob' }
    );
    return response.data;
  }
}

// Export singleton instance
export const billingDependenciesAPI = new BillingDependenciesAPI();

// ============================================================================
// REACT HOOK (to be created separately)
// ============================================================================

export default billingDependenciesAPI;
